// src/features/equipment/components/maintenance/MaintenanceFormDialog.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  CircularProgress,
  Alert,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import ServiceProviderField from './ServiceProviderField';

/**
 * Small form dialog shared by the repair actions (report, send to repair,
 * record, resolve, complete, close). `fields` describes the inputs:
 *   { name, label, required?, multiline?,
 *     type?: 'text' | 'select' | 'radio' | 'datetime' | 'contact',
 *     options?: [{ value, label }], helperText?, maxLength? }
 * 'contact' is free text that suggests the department's contacts; a picked
 * contact's id goes in `<name>_contact_id`.
 * `onSubmit(values)` returns a promise of { success }; the dialog closes on success.
 * `extra` renders below the fields (e.g. an attachment picker); `notice`
 * renders as an info alert above them.
 */
export default function MaintenanceFormDialog({
  open,
  onClose,
  title,
  description,
  fields,
  initialValues = {},
  submitLabel,
  submitColor = 'primary',
  onSubmit,
  extra,
  notice,
}) {
  const { t } = useTranslation(['common']);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      const defaults = Object.fromEntries(fields.map(f => [f.name, f.type === 'datetime' ? null : '']));
      setValues({ ...defaults, ...initialValues });
      setTouched(false);
    }
    // Only reset when the dialog opens
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const missing = fields.filter(f => f.required && !String(values[f.name] ?? '').trim());

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched(true);
    if (missing.length) return;
    setSubmitting(true);
    try {
      // Send empty optional text as null rather than ""
      const payload = Object.fromEntries(Object.entries(values).map(([k, v]) =>
        [k, typeof v === 'string' ? (v.trim() || null) : v]
      ));
      const result = await onSubmit(payload);
      if (result?.success) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const setValue = (name, value) => setValues(prev => ({ ...prev, [name]: value }));

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {description && <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>}
          {notice && <Alert severity="info" sx={{ mb: 2 }}>{notice}</Alert>}
          <Stack spacing={2} sx={{ pt: 1 }}>
            {fields.map((field) => {
              const error = touched && field.required && !String(values[field.name] ?? '').trim();
              const autoFocus = field === fields[0] && !['select', 'radio'].includes(field.type);
              if (field.type === 'radio') {
                return (
                  <FormControl key={field.name} required={field.required} error={error} disabled={submitting}>
                    <FormLabel>{field.label}</FormLabel>
                    <RadioGroup value={values[field.name] ?? ''} onChange={(e) => setValue(field.name, e.target.value)}>
                      {field.options.map(option => (
                        <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} />
                      ))}
                    </RadioGroup>
                    {error && <FormHelperText>{t('common:validation.required')}</FormHelperText>}
                  </FormControl>
                );
              }
              if (field.type === 'contact') {
                return (
                  <ServiceProviderField
                    key={field.name}
                    label={field.label}
                    value={values[field.name]}
                    onChange={(text, contact) => setValues(prev => ({
                      ...prev, [field.name]: text, [`${field.name}_contact_id`]: contact?.contact_id ?? null,
                    }))}
                    disabled={submitting}
                    autoFocus={autoFocus}
                  />
                );
              }
              if (field.type === 'datetime') {
                return (
                  <DateTimePicker
                    key={field.name}
                    label={field.label}
                    value={values[field.name] ?? null}
                    onChange={(value) => setValue(field.name, value)}
                    disableFuture
                    disabled={submitting}
                    slotProps={{ textField: { fullWidth: true, helperText: field.helperText } }}
                  />
                );
              }
              return (
                <TextField
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValue(field.name, e.target.value)}
                  required={field.required}
                  error={error}
                  helperText={error ? t('common:validation.required') : field.helperText}
                  select={field.type === 'select'}
                  multiline={field.multiline}
                  minRows={field.multiline ? 3 : undefined}
                  disabled={submitting}
                  autoFocus={autoFocus}
                  slotProps={{ htmlInput: { maxLength: field.maxLength ?? (field.multiline ? 5000 : 255) } }}
                  fullWidth
                >
                  {field.type === 'select' && field.options.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              );
            })}
            {extra}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={submitting}>{t('common:buttons.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            color={submitColor}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
